import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { Store as StoreType } from "../../../../__generated__/graphql";
import { useCart } from "../../../../core/cart";
import { useEffect, useState } from "react";
import Partition from "../../../../components/common/partition";
import TransitionWrapper from "../../../../components/common/transition-wrapper";
import TimeSaleSection from "../../../../components/store/time-sale-section";
import ReserveSection from "../../../../components/store/reserve-section";
import StoreDetails from "../../../../components/store/store-details";
import StoreHeader from "../../../../components/store/store-header";
import Skeleton from "react-loading-skeleton";
import { notify } from "../../../../utils/toast";
import {
  isPackSetted,
  isTimeSaling,
  isReserveTimeEnabled,
  isPackSoldOut,
} from "../../../../utils/store-expose";
import {
  getDayOfWeekIndex,
  getTomorrowDayOfWeekIndex,
} from "../../../../utils/reserve-time";
import { GET_STORE, GET_PACK } from "../../../../api/store-queries";
import Header from "../../../../components/common/header";

const BossStorePreviewDetail = () => {
  const { id } = useParams();
  const { data, loading } = useQuery(GET_STORE, {
    variables: {
      storeId: Number(id),
    },
    fetchPolicy: "network-only",
  });
  const { data: packData, loading: packLoading } = useQuery(GET_PACK, {
    variables: {
      storeId: Number(id),
    },
  });
  const store = data?.store;
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [activeTab, setActiveTab] = useState("예약제");

  const dayOfWeek = getDayOfWeekIndex(new Date());
  const tomorrowDayOfWeek = getTomorrowDayOfWeekIndex();

  const isPackSettedFlag = isPackSetted(store);
  const isTimeSalingFlag = isTimeSaling(store);
  const isReserveTimeEnabledFlag = isReserveTimeEnabled(
    store?.reserveTime as Record<string, { enabled?: boolean }> | undefined,
    dayOfWeek,
    tomorrowDayOfWeek
  );
  const isPackSoldOutFlag = isPackSoldOut(store);

  useEffect(() => {
    if (isPackSettedFlag) {
      setActiveTab("예약제");
    } else {
      setActiveTab("알림제");
    }
    if (isTimeSalingFlag && !store?.isProductAllSoldOut) {
      setActiveTab("알림제");
    }
  }, [isTimeSalingFlag, isPackSettedFlag, store?.isProductAllSoldOut]);

  useEffect(() => {
    const checkStoreStatus = () => {
      if (store?.closingHours) {
        const closingTime = new Date();

        const [closingHoursHour, closingHoursMinute] = store?.closingHours
          .split(":")
          .map(Number);
        closingTime.setHours(closingHoursHour);
        closingTime.setMinutes(closingHoursMinute);
        closingTime.setSeconds(0);
      }
    };

    checkStoreStatus();

    const interval = setInterval(checkStoreStatus, 60000);

    const calculateTimeLeft = () => {
      if (store?.closingHours) {
        const currentTime = new Date();
        const closingTime = new Date();

        const [closingHoursHour, closingHoursMinute] = store?.closingHours
          .split(":")
          .map(Number);
        closingTime.setHours(closingHoursHour);
        closingTime.setMinutes(closingHoursMinute);
        closingTime.setSeconds(0);

        const difference = closingTime.getTime() - currentTime.getTime();

        if (difference > 0) {
          const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
          const minutes = Math.floor((difference / (1000 * 60)) % 60);
          const seconds = Math.floor((difference / 1000) % 60);

          setTimeLeft(
            `${hours.toString().padStart(2, "0")}:${minutes
              .toString()
              .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
          );
        } else {
          setTimeLeft("00:00:00");
        }
      }
    };

    calculateTimeLeft();
    const intervalTimeLeft = setInterval(calculateTimeLeft, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(intervalTimeLeft);
    };
  }, [store?.closingHours]);

  const { cart } = useCart();

  const getTotalAmount = () => {
    return cart.reduce((total, item) => {
      const price = item.product.userPrice || item.product.price;
      return total + price * item.quantity;
    }, 0);
  };

  const getTotalDisount = () => {
    return cart.reduce((total, item) => {
      const discount = item.product?.userPrice
        ? item.product.price - item?.product?.userPrice
        : 0;
      return total + discount * item.quantity;
    }, 0);
  };

  if (loading || packLoading) {
    return (
      <div className="container mx-auto bg-[#f6f6f6] p-1">
        <div className="relative">
          <Skeleton className="w-full h-[200px] rounded-xl" />
          <div className="absolute bottom-4 left-4 space-y-2 ">
            <Skeleton className="w-32 h-6 rounded-xl" />
            <Skeleton className="w-24 h-4 rounded-xl" />
          </div>
        </div>

        <div className="p-4 space-y-4 bg-white">
          <div className="flex items-center space-x-2">
            <Skeleton className="w-16 h-16 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="w-40 h-5 rounded-xl" />
              <Skeleton className="w-32 h-4 rounded-xl" />
            </div>
          </div>
          <Skeleton className="w-full h-20" />
        </div>

        <div className="mt-4 px-4 pb-4">
          <Skeleton className="w-full h-14 rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto bg-[#f6f6f6]">
      <div className="bg-white">
        <Header title="내 매장 노출 보기 (상세)" showBackButton />
        <div className="flex justify-center items-center border-b border-gray-200 p-2">
          <div className="font-semibold text-xl">
            "<span className="font-bold">실제 고객에게 보이는 화면</span>
            이에요!"
          </div>
        </div>
      </div>

      {store && (
        <>
          <StoreHeader
            loading={loading}
            store={store as StoreType}
            onClickLike={(storeId: number, isLiked: boolean | null) => {
              return Promise.resolve();
            }}
          />

          <StoreDetails loading={loading} store={store as StoreType} />

          <Partition color="light" height="thick" />
          {isPackSettedFlag && (
            <>
              <div className="w-full px-5 py-4">
                <div className="w-full flex rounded-[0.625rem] overflow-hidden border border-[#F9F9F9] bg-white shadow-[0_3px_8px_0_rgba(0,0,0,0.05)]">
                  <div className="relative flex w-full">
                    <div
                      className={`w-[50%] text-center py-4 text-base font-semibold cursor-pointer transition-all duration-300 ease-in-out ${
                        activeTab === "예약제"
                          ? "bg-green-500 text-white"
                          : "bg-transparent text-gray-600 hover:text-gray-800"
                      }`}
                      onClick={() => setActiveTab("예약제")}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg">🥐</span>
                        <span>예약구매</span>
                      </div>
                    </div>
                    <div
                      className={`w-[50%] text-center py-4 text-base font-semibold cursor-pointer transition-all duration-300 ease-in-out ${
                        activeTab === "알림제"
                          ? "bg-blue-500 text-white"
                          : "bg-transparent text-gray-600 hover:text-gray-800"
                      }`}
                      onClick={() => setActiveTab("알림제")}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-lg">⚡</span>
                        <span>실시간구매</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "예약제" ? (
            <ReserveSection
              isTimeSaling={isTimeSalingFlag}
              packData={packData}
              isReserveTimeEnabled={isReserveTimeEnabledFlag}
              isPackSoldOut={isPackSoldOutFlag}
              onClickPackPurchase={() => {
                notify(
                  "내 매장 노출 보기 페이지에서는 지원하지 않는 기능이에요!"
                );
              }}
              store={store}
              dayOfWeek={dayOfWeek}
              isTodayEnabled={store?.pack?.isTodayEnabled}
              isTomorrowEnabled={store?.pack?.isTomorrowEnabled}
              isOverTodayPickupTime={store?.pack?.isOverTodayPickupTime}
              isLiked={false}
              likeCount={store?.likes ? store?.likes?.length : 0}
              onClickNoti={() => {
                notify(
                  "내 매장 노출 보기 페이지에서는 지원하지 않는 기능이에요!"
                );
              }}
              reviewCount={store?.reviewCount || 0}
              isLoggedIn={true}
              onLoginRequired={() => {}}
            />
          ) : (
            <TimeSaleSection
              store={store as StoreType}
              timeLeft={timeLeft}
              cart={cart}
              onClickProduct={() => {
                notify(
                  "내 매장 노출 보기 페이지에서는 지원하지 않는 기능이에요!"
                );
              }}
              onClickLike={() => {
                notify(
                  "내 매장 노출 보기 페이지에서는 지원하지 않는 기능이에요!"
                );
                return Promise.resolve();
              }}
            />
          )}

          {cart.length > 0 && activeTab !== "예약제" && (
            <div
              onClick={() => {
                notify(
                  "내 매장 노출 보기 페이지에서는 지원하지 않는 기능이에요!"
                );
              }}
              className="px-5 py-4 w-full fixed bottom-0 max-w-[742px] bg-white"
            >
              <div className="absolute left-0 top-[-2.7rem] w-full h-[43px] bg-[#282828] rounded-tl-[10px] rounded-tr-[10px] text-white text-sm font-semibold flex justify-center items-center">
                <span className="text-[#fd4242] mr-1">
                  {(getTotalDisount() || 0).toLocaleString()}원
                </span>{" "}
                할인받았어요!
              </div>
              <TransitionWrapper
                scale={0.95}
                opacity={0.8}
                className="w-full h-[60px] flex items-center justify-center bg-[#1562fc] rounded-lg border"
              >
                <div className="text-center flex items-center space-x-1">
                  <span className="text-white font-bold leading-snug">
                    {getTotalAmount().toLocaleString()}원
                  </span>
                  <span className="text-white text-base font-semibold leading-snug">
                    구매하기
                  </span>
                </div>
              </TransitionWrapper>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BossStorePreviewDetail;
